import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUIStore } from '@/store/ui.store';

// Mapeo pathname-prefix → clave de módulo (enviada al backend como modulo_activo)
const ROUTE_TO_MODULE: Array<[string, string]> = [
  ['/ingresos',      'ingresos'],
  ['/egresos',       'egresos'],
  ['/bancos',        'bancos'],
  ['/proyectos',     'proyectos'],
  // I-CRÉDITOS (2026-09-08, Tarea 8): '/creditos' se apagó de verdad (a diferencia de
  // '/egresos'/'/ingresos' arriba, que siguen vivas en paralelo a su mudanza a la casa) —
  // ningún URL puede caer ya en esa ruta, así que esta entrada quedaría muerta.
  ['/crm',           'crm'],
  ['/obra',          'obra'],
  ['/tareas',        'tareas'],
  ['/chats',         'tareas'],
  ['/post-venta',    'postventa'],
  ['/reportes',      'reportes'],
  ['/dashboard',     'dashboard'],
  ['/configuracion', 'configuracion'],
];

function getModuloActivo(pathname: string): string {
  for (const [prefix, modulo] of ROUTE_TO_MODULE) {
    if (pathname.startsWith(prefix)) return modulo;
  }
  return 'general';
}

export interface AIMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
}

export function useAIAssistant() {
  const { pathname } = useLocation();
  const { aiAssistantOpen, toggleAIAssistant, setAIAssistantOpen } = useUIStore();
  const abortRef = useRef<AbortController | null>(null);

  const moduloActivo = getModuloActivo(pathname);

  const sendMessage = useCallback(
    async (
      mensaje: string,
      onChunk: (chunk: string) => void,
      onDone: () => void,
      onError: (msg: string) => void,
    ) => {
      // Cancelar stream previo si existe
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const token = localStorage.getItem('erp_token');

      try {
        const res = await fetch('/api/asistente/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ mensaje, modulo_activo: moduloActivo }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          onError(data.message ?? 'Error al conectar con el asistente');
          return;
        }

        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let doneCalled = false;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.chunk) onChunk(payload.chunk);
              if (payload.done) { onDone(); doneCalled = true; }
              if (payload.error) onError(payload.message ?? 'Error del asistente');
            } catch {
              // línea malformada — ignorar
            }
          }
        }
        // Fallback: si el stream terminó sin emitir { done: true }
        if (!doneCalled) onDone();
      } catch (err: unknown) {
        if ((err as Error).name !== 'AbortError') {
          onError('No se pudo conectar con el asistente. Intenta de nuevo.');
        }
      }
    },
    [moduloActivo],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return {
    aiAssistantOpen,
    toggleAIAssistant,
    setAIAssistantOpen,
    moduloActivo,
    sendMessage,
    cancel,
  };
}
